/**
 * Call Graph Custom Nodes
 * Phase 2E: React Flow Custom Nodes for AST-based Diagrams
 *
 * Custom nodes for visualizing function call relationships.
 */

import { memo, useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { FunctionNodeType } from '@veodiagram/shared';

// ============= Multi-Handle Configuration =============

const MAX_HANDLES_PER_SIDE = 5;

/**
 * Generate multiple handles distributed vertically along a side.
 * This allows edges to connect at different positions, reducing visual crossings.
 */
function generateDistributedHandles(
  type: 'source' | 'target',
  position: Position,
  count: number,
  handleColor: string,
  isDisabled: boolean
): JSX.Element[] {
  if (isDisabled) return [];

  const effectiveCount = Math.max(1, Math.min(count, MAX_HANDLES_PER_SIDE));

  return Array.from({ length: effectiveCount }, (_, i) => {
    // Distribute handles evenly across the height
    // For 1 handle: 50%, for 2: 33%/67%, for 3: 25%/50%/75%, etc.
    const offset = (i + 1) / (effectiveCount + 1);

    return (
      <Handle
        key={`${type}-${i}`}
        id={`${type}-${i}`}
        type={type}
        position={position}
        className="!w-2 !h-2 !border-2 !border-white !min-w-0 !min-h-0"
        style={{
          backgroundColor: handleColor,
          top: `${offset * 100}%`,
          transform: 'translateY(-50%)',
        }}
      />
    );
  });
}

// ============= Node Data Interfaces =============

interface CallGraphNodeData {
  label: string;
  qualifiedName?: string;
  module?: string;
  type: FunctionNodeType;
  isAsync?: boolean;
  isEntryPoint?: boolean;
  isExternal?: boolean;
  description?: string;
  complexity?: {
    cyclomatic: number;
    branches: number;
    loops: number;
  };
  calledCount?: number;
  callCount?: number;
  isHotspot?: boolean;
  // veodiagram-issues#45.B — BCG-specific passthrough. Populated by
  // BackendGraphPage when rendering BCG nodes; ignored on regular
  // call graphs. Lets the renderer differentiate ExternalCall
  // (network HTTP / SDK) from DbCall (database) without a schema
  // change to FunctionNodeType.
  bcgKind?: 'DbCall' | 'ExternalCall';
  /** For ExternalCall: SDK package or service name (e.g. 'stripe'). */
  service?: string;
  /** For ExternalCall.category=='http': resolved/partial URL pattern. */
  urlPattern?: string;
  /** Extracted hostname when urlPattern is absolute. Renderer shows
   *  this above the path as a quick scan target. */
  host?: string;
  /** ExternalCall discriminator from #37 — controls styling intensity:
   *  http leaves the system (strongest signal); sdk stays in-process. */
  category?: 'sdk' | 'http';
  [key: string]: unknown;
}

// ============= Function Type Colors =============

const FUNCTION_TYPE_COLORS: Record<FunctionNodeType, { bg: string; border: string; text: string; handle: string }> = {
  function: { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1', handle: '#1976d2' },
  method: { bg: '#e8f5e9', border: '#388e3c', text: '#1b5e20', handle: '#388e3c' },
  arrow: { bg: '#f3e5f5', border: '#7b1fa2', text: '#4a148c', handle: '#7b1fa2' },
  constructor: { bg: '#fff8e1', border: '#ff8f00', text: '#e65100', handle: '#ff8f00' },
  entry: { bg: '#c8e6c9', border: '#2e7d32', text: '#1b5e20', handle: '#2e7d32' },
  external: { bg: '#ffebee', border: '#c62828', text: '#b71c1c', handle: '#c62828' },
  critical: { bg: '#ffcdd2', border: '#b71c1c', text: '#7f0000', handle: '#b71c1c' },
};

// veodiagram-issues#45.B — BCG-only colour overrides. Applied when
// `data.bcgKind` is set, so generic CallGraph rendering is unaffected.
// Four distinct treatments:
//   DbCall                  → emerald / database (sibling system, stays in
//                             trust-boundary; teal-shifted so it doesn't
//                             collide with the generic `method` green —
//                             R1#2 from PR #1108 review)
//   ExternalCall/http       → gray outlined / network arrow (leaves the system)
//   ExternalCall/sdk        → muted gray / in-process external library
//   ExternalCall/unknown    → R1#1 from PR #1108 review — analyzer-incomplete
//                             nodes (category undefined) keep the
//                             original-red palette so the data gap is
//                             visible rather than silently muted to SDK.
const BCG_KIND_COLORS = {
  DbCall:                { bg: '#d1fae5', border: '#0f766e', text: '#134e4a', handle: '#0f766e' },
  ExternalCallHttp:      { bg: '#f5f5f5', border: '#616161', text: '#424242', handle: '#616161' },
  ExternalCallSdk:       { bg: '#fafafa', border: '#9e9e9e', text: '#616161', handle: '#9e9e9e' },
  ExternalCallUnknown:   { bg: '#ffebee', border: '#c62828', text: '#b71c1c', handle: '#c62828' },
} as const;

function resolveBcgColors(
  data: Pick<CallGraphNodeData, 'bcgKind' | 'category'>,
): { bg: string; border: string; text: string; handle: string } | null {
  if (data.bcgKind === 'DbCall') return BCG_KIND_COLORS.DbCall;
  if (data.bcgKind === 'ExternalCall') {
    if (data.category === 'http') return BCG_KIND_COLORS.ExternalCallHttp;
    if (data.category === 'sdk') return BCG_KIND_COLORS.ExternalCallSdk;
    return BCG_KIND_COLORS.ExternalCallUnknown;
  }
  return null;
}

// ============= Icons =============

const FunctionIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
  </svg>
);

const MethodIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const ArrowIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const ConstructorIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const EntryIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const ExternalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const CriticalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

// veodiagram-issues#45.B — cylinder/database icon for BCG DbCall nodes.
// R1#10 — accessible name + data-testid so screen-readers can announce
// the kind and tests can assert the icon's presence.
const DatabaseIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    role="img"
    aria-label="Database call"
    data-testid="bcg-db-icon"
  >
    <title>Database call</title>
    <ellipse cx="12" cy="5" rx="8" ry="2.5" strokeWidth={2} />
    <path strokeWidth={2} strokeLinecap="round" d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
    <path strokeWidth={2} strokeLinecap="round" d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
  </svg>
);

const getTypeIcon = (type: FunctionNodeType) => {
  switch (type) {
    case 'function': return <FunctionIcon />;
    case 'method': return <MethodIcon />;
    case 'arrow': return <ArrowIcon />;
    case 'constructor': return <ConstructorIcon />;
    case 'entry': return <EntryIcon />;
    case 'external': return <ExternalIcon />;
    case 'critical': return <CriticalIcon />;
    default: return <FunctionIcon />;
  }
};

// ============= Base Call Graph Node =============

interface BaseCallGraphNodeProps {
  data: CallGraphNodeData;
  selected: boolean;
}

function BaseCallGraphNode({ data, selected }: BaseCallGraphNodeProps) {
  // #45.B — BCG kind-specific colors take precedence over the generic
  // type color, so DbCall and ExternalCall nodes are visually distinct
  // when rendered from BackendGraphPage.
  const bcgColors = resolveBcgColors(data);
  const colors = bcgColors ?? FUNCTION_TYPE_COLORS[data.type] ?? FUNCTION_TYPE_COLORS.function;
  const isEntryPoint = data.isEntryPoint || data.type === 'entry';
  const isExternal = data.isExternal || data.type === 'external';
  const isBcgExternalHttp = data.bcgKind === 'ExternalCall' && data.category === 'http';
  const isBcgDbCall = data.bcgKind === 'DbCall';

  // Generate multiple handles based on connection counts
  // For LR layout: targets on LEFT, sources on RIGHT
  const targetHandles = useMemo(() => {
    const count = data.calledCount || 1; // Number of callers
    return generateDistributedHandles('target', Position.Left, count, colors.handle, isEntryPoint);
  }, [data.calledCount, colors.handle, isEntryPoint]);

  const sourceHandles = useMemo(() => {
    const count = data.callCount || 1; // Number of callees
    return generateDistributedHandles('source', Position.Right, count, colors.handle, isExternal);
  }, [data.callCount, colors.handle, isExternal]);

  return (
    <div
      className={`
        relative flex flex-col px-3 py-2 min-w-[140px] max-w-[200px]
        rounded-lg border-2 transition-all duration-200
        ${selected ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg' : 'shadow-sm'}
        ${isEntryPoint ? 'border-l-4' : ''}
      `}
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.border,
        borderLeftColor: isEntryPoint ? '#2e7d32' : colors.border,
      }}
    >
      {/* Multi-handles for reduced edge crossings */}
      {targetHandles}
      {sourceHandles}

      {/* Header */}
      <div className="flex items-center gap-2">
        <div style={{ color: colors.text }}>
          {/* #45.B — BCG kind overrides the generic type icon: DB cylinder
              for DbCall, external arrow for ExternalCall (already covered
              by ExternalIcon via the type='external' fallback for the
              non-BCG case). */}
          {isBcgDbCall ? <DatabaseIcon /> : getTypeIcon(data.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-semibold text-xs truncate font-mono"
            style={{ color: colors.text }}
            title={data.qualifiedName || data.label}
          >
            {data.label}
          </div>
          {/* #45.B — for ExternalCall HTTP, the host is more scan-worthy
              than the source-file module. Show host as the secondary
              line; fall back to module for everything else. */}
          {isBcgExternalHttp && data.host ? (
            <div
              className="text-[10px] text-gray-600 truncate"
              title={data.urlPattern || data.host}
            >
              {data.host}
            </div>
          ) : (
            data.module && (
              <div className="text-[10px] text-gray-500 truncate" title={data.module}>
                {data.module.split('/').pop()}
              </div>
            )
          )}
        </div>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1 mt-1.5">
        {/* #45.B — category pill for BCG ExternalCall, surfaces the
            #37 sdk-vs-http distinction. HTTP is the "leaves the
            system" case and gets the prominent treatment; SDK stays
            muted because the call is in-process. */}
        {data.bcgKind === 'ExternalCall' && data.category === 'http' && (
          <span
            className="text-[9px] px-1 py-0.5 rounded bg-gray-200 text-gray-700 border border-gray-300"
            title={data.urlPattern ? `Outbound HTTP → ${data.urlPattern}` : 'Outbound HTTP call'}
          >
            ↗ HTTP
          </span>
        )}
        {data.bcgKind === 'ExternalCall' && data.category === 'sdk' && (
          <span
            className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-600"
            title={data.service ? `SDK method on ${data.service}` : 'External SDK method'}
          >
            SDK
          </span>
        )}
        {/* R1#1 fix — analyzer-incomplete external call (category missing).
            Render a neutral 'External' pill so the user sees there's a
            data gap, rather than silently inheriting SDK styling and
            obscuring the unknown classification. */}
        {data.bcgKind === 'ExternalCall' &&
          data.category !== 'http' &&
          data.category !== 'sdk' && (
            <span
              className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700 border border-red-300"
              title="External call (category not detected by analyzer)"
            >
              External (?)
            </span>
          )}
        {data.isAsync && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-700">
            async
          </span>
        )}
        {data.type === 'constructor' && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700">
            ctor
          </span>
        )}
      </div>

      {/* Stats */}
      {(data.calledCount !== undefined || data.callCount !== undefined) && (
        <div className="flex gap-2 mt-1 text-[10px] text-gray-500">
          {data.calledCount !== undefined && data.calledCount > 0 && (
            <span title="Called by">←{data.calledCount}</span>
          )}
          {data.callCount !== undefined && data.callCount > 0 && (
            <span title="Calls">→{data.callCount}</span>
          )}
        </div>
      )}

      {/* Complexity indicator */}
      {data.complexity && data.complexity.cyclomatic > 10 && (
        <div
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
          style={{
            backgroundColor: data.complexity.cyclomatic > 20 ? '#b71c1c' : '#ef6c00',
          }}
          title={`Cyclomatic complexity: ${data.complexity.cyclomatic}`}
        >
          {data.complexity.cyclomatic}
        </div>
      )}

      {/* Hotspot indicator */}
      {data.isHotspot && (
        <div
          className="absolute -bottom-2 -right-2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs"
          title="Hotspot: called by many functions"
        >
          🔥
        </div>
      )}
    </div>
  );
}

// ============= Specialized Call Graph Nodes =============

export const FunctionNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'function' } as CallGraphNodeData} selected={selected} />
));
FunctionNode.displayName = 'FunctionNode';

export const MethodNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'method' } as CallGraphNodeData} selected={selected} />
));
MethodNode.displayName = 'MethodNode';

export const ArrowFunctionNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'arrow' } as CallGraphNodeData} selected={selected} />
));
ArrowFunctionNode.displayName = 'ArrowFunctionNode';

export const ConstructorNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'constructor' } as CallGraphNodeData} selected={selected} />
));
ConstructorNode.displayName = 'ConstructorNode';

export const EntryFunctionNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'entry', isEntryPoint: true } as CallGraphNodeData} selected={selected} />
));
EntryFunctionNode.displayName = 'EntryFunctionNode';

export const ExternalCallNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'external', isExternal: true } as CallGraphNodeData} selected={selected} />
));
ExternalCallNode.displayName = 'ExternalCallNode';

export const CriticalFunctionNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, type: 'critical' } as CallGraphNodeData} selected={selected} />
));
CriticalFunctionNode.displayName = 'CriticalFunctionNode';

export const HotspotNode = memo(({ data, selected }: NodeProps) => (
  <BaseCallGraphNode data={{ ...data, isHotspot: true } as CallGraphNodeData} selected={selected} />
));
HotspotNode.displayName = 'HotspotNode';

export const CallGraphGenericNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as CallGraphNodeData;
  return <BaseCallGraphNode data={nodeData} selected={selected} />;
});
CallGraphGenericNode.displayName = 'CallGraphGenericNode';

// ============= Node Type Registry =============

export const callGraphNodeTypes = {
  function: FunctionNode,
  method: MethodNode,
  arrow: ArrowFunctionNode,
  constructor: ConstructorNode,
  entry: EntryFunctionNode,
  external: ExternalCallNode,
  critical: CriticalFunctionNode,
  hotspot: HotspotNode,
  callGraphNode: CallGraphGenericNode,
};

/**
 * Get the appropriate node type string for a function type.
 */
export function getCallGraphNodeType(type: FunctionNodeType, isHotspot?: boolean): string {
  if (isHotspot) return 'hotspot';
  if (type in callGraphNodeTypes) {
    return type;
  }
  return 'function';
}
